import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnInit, Output } from "@angular/core";
import { Team } from "@common/ADAPT.Common.Model/organisation/team";
import { Person } from "@common/ADAPT.Common.Model/person/person";
import { RouteEventsService } from "@common/route/route-events.service";
import { UrlFilterService } from "@common/shell/filter/url-filter.service";
import { BaseComponent } from "@common/ux/base.component/base.component";
import isEqual from "lodash.isequal";
import moment from "moment";
import { BehaviorSubject, filter, Subject } from "rxjs";
import { IMeetingFilterParams } from "../meetings.service";

export enum MeetingDisplayOptions {
    displayAgendaItemDescriptions = "displayAgendaItemDescriptions",
    displayAllAgendaItems = "displayAllAgendaItems",
}

// All possible keys from IMeetingFilterParams interface
export const MeetingFilterParamKeys: (keyof IMeetingFilterParams)[] = [
    "peopleIds",
    "showDecisions",
    "showMinutes",
    "showActions",
    "showObjectives",
    "fromDate",
    "toDate",
    MeetingDisplayOptions.displayAgendaItemDescriptions,
    MeetingDisplayOptions.displayAllAgendaItems,
];

export const DefaultMeetingFilter: IMeetingFilterParams = {
    peopleIds: [],
    showDecisions: false,
    showMinutes: false,
    showActions: false,
    showObjectives: false,
    fromDate: moment().subtract(1, "year").startOf("month").toDate(), // default to start of month one year ago
    displayAgendaItemDescriptions: true,
    displayAllAgendaItems: true,
};

@Component({
    selector: "adapt-meetings-filter",
    standalone: false,
    changeDetection: ChangeDetectionStrategy.Eager,
    templateUrl: "./meetings-filter.component.html",
})
export class MeetingsFilterComponent extends BaseComponent implements OnInit {
    public readonly MeetingFilterParamKeys = MeetingFilterParamKeys;

    @Input() public team?: Team;
    @Output() public meetingFilterChanged = new EventEmitter<IMeetingFilterParams | undefined>();

    public currentFilter: IMeetingFilterParams = { ...DefaultMeetingFilter };
    public isDefaultFilter = true;
    public filterChanged = new Subject<IMeetingFilterParams>();
    private isDefaultFilterUpdater = this.createThrottledUpdater((isDefault: boolean) => this.isDefaultFilter = isDefault);
    private updatingFilterUrl = new BehaviorSubject<boolean>(false);

    public constructor(
        private urlFilterService: UrlFilterService,
        private routeEventsService: RouteEventsService,
    ) {
        super();
    }

    public ngOnInit() {
        this.filterChanged.pipe(
            this.takeUntilDestroyed(),
        ).subscribe((meetingFilter: IMeetingFilterParams) => {
            const isDefault = isEqual(meetingFilter, DefaultMeetingFilter);
            this.isDefaultFilterUpdater.next(isDefault);
            this.updateFilterUrl(meetingFilter);
            this.meetingFilterChanged.emit(isDefault ? undefined : meetingFilter);
        });

        this.routeEventsService.searchParameterChanged$.pipe(
            filter(() => !this.updatingFilterUrl.value),
            this.takeUntilDestroyed(),
        ).subscribe(() => this.updateFromUrl());
    }

    public emitFilterChanged() {
        if (!this.currentFilter.showActions && !this.currentFilter.showDecisions && !this.currentFilter.showMinutes && !this.currentFilter.showObjectives) {
            // if no options are selected, reset to default filter
            this.currentFilter.displayAllAgendaItems = DefaultMeetingFilter.displayAllAgendaItems;
        }

        this.filterChanged.next(this.currentFilter);
    }

    public fromDateChanged(date?: Date) {
        this.currentFilter.fromDate = date;
        if (this.currentFilter.toDate && this.currentFilter.fromDate && moment(this.currentFilter.toDate).isBefore(this.currentFilter.fromDate)) {
            this.currentFilter.toDate = undefined; // reset toDate if it is before fromDate
        }

        this.emitFilterChanged();
    }

    public resetOptions() {
        this.currentFilter = { ...DefaultMeetingFilter };
        this.filterChanged.next(this.currentFilter);
    }

    public onPeopleSelectionChanged(people: Person[]) {
        const selection = people.map((p) => p.personId);
        if (!isEqual(this.currentFilter.peopleIds?.sort(), selection?.sort())) {
            this.currentFilter.peopleIds = people.map((p) => p.personId); // selection is already sorted - so use the unsorted array from callback
            this.emitFilterChanged();
        }
    }

    private updateFilterUrl(meetingFilter: IMeetingFilterParams) {
        this.updatingFilterUrl.next(true);
        this.urlFilterService.setFilterToUrl<IMeetingFilterParams>(MeetingFilterParamKeys, meetingFilter, DefaultMeetingFilter,
            (key, stringifiedValue) => {
                // Special handling for fromDate - if it is undefined, we want to set it to "undefined" in the URL
                // Otherwise, we want to stringify the value, undefined -> default value and won't be shown in the URL
                if (key === "fromDate" && !stringifiedValue) {
                    return "undefined";
                }
                return stringifiedValue;
            }).then(() => this.updatingFilterUrl.next(false));
    }

    private updateFromUrl() {
        this.currentFilter = this.urlFilterService.getFiltersFromUrl<IMeetingFilterParams>(MeetingFilterParamKeys, DefaultMeetingFilter);
        this.filterChanged.next(this.currentFilter);
    }
}
